import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { Time } from 'sushiweb/Icons';
import Layout from 'sushiweb/Layout';
import Modal from 'sushiweb/Modal/Modal';

import {
  BudgetPerEmployeeIcon,
  ApplicableDaysIcon,
  DotIcon,
  UnionIcon,
  LocationIcon,
  FoodPlatterIcon,
} from '../../../../../components/SVGs/commonSVGs';

import {
  PolicyContainer,
  PolicyHeader,
  PolicyTitle,
  ButtonsContainer,
  PolicyButton,
  PolicyDetailsRow,
  PolicyDetailItem,
  SlotContainer,
  SlotHeader,
  SlotTitle,
  SlotDetailsRow,
  ClickableText,
  StyledLayout,
  ModalContentContainer,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalList,
  ModalListItem,
  ModalListItemText,
  ModalFooter,
  ModalCloseButton,
} from './style';

import { ApplicabilityRule, FREQUENCY_SUFFIX } from '../constants';
import { frequencyTypes } from '../../../../WalletPrograms/constants';
import { getQuestionLabel } from '../../../steps/CreatePolicy/createPolicyForm/util';


const PolicySnippet = ({
  policyTitle,
  buttons,
  slots,
  applicableServices,
  applicableDays,
  budgetPerEmployee,
  cycleType,
  policyQuestions,
  onModalOpen,
  onModalClose,
}) => {
  const [showPolicyQuestionsModal, setShowPolicyQuestionsModal] = useState(false);
  const [showPolicyLocationsModal, setShowPolicyLocationsModal] = useState(false);
  const [currentSlotLocations, setCurrentSlotLocations] = useState([]);
  const [currentSlotApplicabilityRule, setCurrentSlotApplicabilityRule] = useState(0);

  const openModal = setter => {
    setter(true);
    if (onModalOpen) onModalOpen();
  };

  const closeModal = setter => {
    setter(false);
    if (onModalClose) onModalClose();
  };

  const getFrequencySuffix = () => {
    switch (cycleType) {
      case frequencyTypes.Daily:
        return FREQUENCY_SUFFIX.PER_DAY;
      case frequencyTypes.Weekly:
        return FREQUENCY_SUFFIX.PER_WEEK;
      default:
        return FREQUENCY_SUFFIX.PER_MONTH;
    }
  };

  const getLocationModalTitle = aplRule => {
    switch (aplRule) {
      case ApplicabilityRule.APPLICABILITY_RULE_EXCLUSIVE:
        return 'Prohibited locations';
      default:
        return 'Permitted locations';
    }
  };

  const handleQuestionsKeyDown = e => {
    if (e.key === 'Enter' || e.key === ' ') {
      openModal(setShowPolicyQuestionsModal);
    }
  };

  const handleSlotLocationClick = (slotLocations, applicabilityRule) => {
    setCurrentSlotLocations(slotLocations || []);
    setCurrentSlotApplicabilityRule(applicabilityRule || 0);
    openModal(setShowPolicyLocationsModal);
  };

  const renderLocationModal = () => {
    return (
      <Modal
        visible={showPolicyLocationsModal}
        onClose={() => closeModal(setShowPolicyLocationsModal)}
        isCentered
        type='flex'
        borderRadius='20px'
      >
        <ModalContentContainer>
          <ModalHeader>
            <ModalTitle>
              {getLocationModalTitle(currentSlotApplicabilityRule)}
            </ModalTitle>
          </ModalHeader>
          <ModalContent>
            <ModalList>
              {currentSlotLocations?.map((location, index) => (
                <ModalListItem
                  key={location?.poi_id || index}
                  isLast={index === currentSlotLocations?.length - 1}
                >
                  <LocationIcon />
                  <ModalListItemText>
                    {location?.address
                      ? `${location?.address} (${location?.poi_id})`
                      : `POI ${location?.poi_id}`}
                  </ModalListItemText>
                </ModalListItem>
              ))}
            </ModalList>
          </ModalContent>
          <ModalFooter>
            <ModalCloseButton
              type='button'
              onClick={() => closeModal(setShowPolicyLocationsModal)}
            >
              Go Back
            </ModalCloseButton>
          </ModalFooter>
        </ModalContentContainer>
      </Modal>
    );
  };

  const renderQuestionModal = () => {
    return (
      <Modal
        visible={showPolicyQuestionsModal}
        onClose={() => closeModal(setShowPolicyQuestionsModal)}
        isCentered
        type='flex'
        borderRadius='12px'
      >
        <ModalContentContainer>
          <ModalHeader>
            <ModalTitle>Questions added</ModalTitle>
          </ModalHeader>
          <ModalContent>
            <ModalList>
              {policyQuestions?.map((question, index) => (
                <ModalListItem
                  key={question?.id || index}
                  isLast={index === policyQuestions?.length - 1}
                >
                  <UnionIcon />
                  <ModalListItemText>
                    {getQuestionLabel(question)}
                  </ModalListItemText>
                </ModalListItem>
              ))}
            </ModalList>
          </ModalContent>
          <ModalFooter>
            <ModalCloseButton
              type='button'
              onClick={() => closeModal(setShowPolicyQuestionsModal)}
            >
              Go Back
            </ModalCloseButton>
          </ModalFooter>
        </ModalContentContainer>
      </Modal>
    );
  };

  return (
    <PolicyContainer>
      <StyledLayout>
        <Layout.Item colD={12} colT={12} colM={12}>
          <PolicyHeader>
            <PolicyTitle>{policyTitle}</PolicyTitle>
            {buttons && buttons?.length > 0 && (
              <ButtonsContainer>
                {buttons?.map((button, index) => (
                  <React.Fragment key={button.label}>
                    <PolicyButton type='button' onClick={button?.onClick}>
                      {button?.label}
                    </PolicyButton>
                    {index < buttons?.length - 1 && <DotIcon />}
                  </React.Fragment>
                ))}
              </ButtonsContainer>
            )}
          </PolicyHeader>
        </Layout.Item>
        <Layout.Item colD={12} colT={12} colM={12}>
          <PolicyDetailsRow>
            <PolicyDetailItem>
              <FoodPlatterIcon />
              <span>Applicable services:</span>
              {applicableServices?.join(', ')}
            </PolicyDetailItem>

            <PolicyDetailItem>
              <ApplicableDaysIcon />
              <span>Applicable days:</span>
              {applicableDays?.join(', ')}
            </PolicyDetailItem>

            <PolicyDetailItem>
              <BudgetPerEmployeeIcon />
              <span>Budget per employee:</span>
              {`₹${budgetPerEmployee} ${getFrequencySuffix()}`}
            </PolicyDetailItem>

            <PolicyDetailItem>
              <UnionIcon />
              <span>Questions to employees: </span>
              {policyQuestions?.length > 0 ? (
                <ClickableText
                  onClick={() => openModal(setShowPolicyQuestionsModal)}
                  role='button'
                  tabIndex={0}
                  onKeyDown={handleQuestionsKeyDown}
                >
                  {`${policyQuestions?.length} question(s)`}
                </ClickableText>
              ) : (
                'NA'
              )}
            </PolicyDetailItem>
          </PolicyDetailsRow>
        </Layout.Item>
        {slots?.map(slot => (
          <Layout.Item
            key={`slot-${slot?.title}`}
            colD={12}
            colT={12}
            colM={12}
          >
            <SlotContainer>
              <SlotHeader>
                <Time />
                <SlotTitle>
                  <span>{slot?.title}</span>
                  {slot?.time}
                </SlotTitle>
              </SlotHeader>

              <SlotDetailsRow>
                <span>Slot level budget:</span>
                {`₹${slot?.slotLevelBudget}`}
                <DotIcon />
                <span>Per order budget:</span>
                {slot?.perOrderBudget > 0 ? `₹${slot?.perOrderBudget}` : 'NA'}
                <DotIcon />
                <span>Slot level days:</span>
                {slot?.slotLevelDays?.join(', ')}
                <DotIcon />
                <span>Location restriction: </span>
                {slot?.slotLocations && slot?.slotLocations?.length > 0 ? (
                  <ClickableText
                    onClick={() => handleSlotLocationClick(slot?.slotLocations, slot?.applicabilityRule)}
                    role='button'
                    tabIndex={0}
                    onKeyDown={
                      e => {
                        if (e.key === 'Enter')
                          handleSlotLocationClick(slot?.slotLocations, slot?.applicabilityRule);
                      }
                    }
                  >
                    {slot?.locationRestriction}
                  </ClickableText>
                ) : (
                  'NA'
                )}
              </SlotDetailsRow>
            </SlotContainer>
          </Layout.Item>
        ))}
      </StyledLayout>

      {showPolicyQuestionsModal &&
        policyQuestions?.length > 0 &&
        renderQuestionModal()}

      {showPolicyLocationsModal &&
        currentSlotLocations?.length > 0 &&
        renderLocationModal()}
    </PolicyContainer>
  );
};

PolicySnippet.propTypes = {
  policyTitle: PropTypes.string.isRequired,
  buttons: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
    }),
  ),
  slots: PropTypes.arrayOf(PropTypes.object),
  applicableServices: PropTypes.arrayOf(PropTypes.string),
  applicableDays: PropTypes.arrayOf(PropTypes.string),
  budgetPerEmployee: PropTypes.number,
  cycleType: PropTypes.number,
  policyQuestions: PropTypes.arrayOf(PropTypes.object),
  onModalOpen: PropTypes.func,
  onModalClose: PropTypes.func,
};

PolicySnippet.defaultProps = {
  buttons: [],
  slots: [],
  applicableServices: [],
  applicableDays: [],
  budgetPerEmployee: 0,
  cycleType: 2,
  policyQuestions: [],
  onModalOpen: null,
  onModalClose: null,
};

export default PolicySnippet;
